import { Component, EventEmitter, Optional, Output, Self, ViewEncapsulation } from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { LifeChangeItem, LifeChangeStatus } from '@shared/models/step.model';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { I18nService } from '@app/i18n';
import { Key } from '@app/components/pl-input/pl-input.component';
import { getDateTimeNowAsString } from '@app/utils/date';

interface StatusLabel {
  value: LifeChangeStatus;
  label: string;
}

@Component({
  selector: 'pl-life-change',
  templateUrl: './lifeChange.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class LifeChangeComponent implements ControlValueAccessor {
  @Output() public deleteLifeChange: EventEmitter<void> = new EventEmitter<void>();

  public lifeChangeItem: LifeChangeItem | null = null;
  public titleNgModel: string = '';

  public isEditMode: boolean = false;

  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly LifeChangeStatus: typeof LifeChangeStatus = LifeChangeStatus;

  public statusLabels: StatusLabel[] = this.getStatusLabel();

  private onChangeCallback!: (value: LifeChangeItem | null) => void;
  private onTouchedCallback!: () => void;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private i18nService: I18nService,
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  public registerOnChange(fn: (value: LifeChangeItem | null) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(valueToWrite: LifeChangeItem | undefined | null): void {
    if (valueToWrite) {
      this.lifeChangeItem = {
        ...valueToWrite,
      };

      if (!valueToWrite.title) {
        this.isEditMode = true;
      }
    }
  }

  public emitChanges(valueToEmit: LifeChangeItem | null): void {
    if (valueToEmit) {
      valueToEmit.updatedAt = getDateTimeNowAsString();
    }
    this.onChangeCallback(valueToEmit);
    this.onTouched();
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  private getStatusLabel(): StatusLabel[] {
    return Object.values(LifeChangeStatus)
      .filter((status: string | LifeChangeStatus) => {
        return !Number.isNaN(Number(status));
      })
      .map((status: string | LifeChangeStatus) => {
        return {
          value: status as LifeChangeStatus,
          label: this.i18nService.translate(`blocks.lifeChanges.status.${status}`),
        };
      });
  }

  public onSliderChange(): void {
    this.emitChanges(this.lifeChangeItem);
  }

  public onDeleteLifeChange(): void {
    this.deleteLifeChange.emit();
  }

  public enterEditMode(): void {
    this.isEditMode = true;
    if (this.lifeChangeItem) {
      this.titleNgModel = this.lifeChangeItem?.title;
    }
  }

  public onSpecialKeyPressed(key: Key): void {
    switch (key) {
      case Key.enter:
        return this.onSaveClicked();
      case Key.escape:
        return this.onCancelClicked();
    }
  }

  public onCancelClicked(): void {
    if (this.lifeChangeItem?.title) {
      this.isEditMode = false;
      this.onTouched();
    } else {
      this.onDeleteLifeChange();
    }
  }

  public onSaveClicked(): void {
    if (!!this.titleNgModel) {
      this.isEditMode = false;
      if (this.lifeChangeItem) {
        this.lifeChangeItem.title = this.titleNgModel;
      }
      this.emitChanges(this.lifeChangeItem);
    }
  }
}
